import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';
import CubMascot from '../../components/CubMascot.jsx';
import { Loader, Alert, Stat, ProgressBar } from '../../components/ui.jsx';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

/** One course card. Published courses link through; the rest are locked. */
function CourseCard({ track }) {
  const lessonCount = track.modules.reduce((n, m) => n + m.lessonCount, 0);
  const done = track.modules.reduce((n, m) => n + m.completedCount, 0);
  const finished = lessonCount > 0 && done === lessonCount;

  const body = (
    <>
      <div className="course-top">
        <span className="course-icon" aria-hidden="true">{track.icon}</span>
        {track.isPublished && track.requiresPayment ? (
          <span className="pill pill-warn">🔒 Needs unlocking</span>
        ) : track.isPublished ? (
          finished ? (
            <span className="pill pill-good">🏆 Completed</span>
          ) : done > 0 ? (
            <span className="pill pill-warn">In progress</span>
          ) : (
            <span className="pill pill-good">Ready to start</span>
          )
        ) : (
          <span className="pill">🔒 Coming soon</span>
        )}
      </div>

      <h2 className="course-title">{track.title}</h2>
      <p className="course-tagline">{track.tagline}</p>
      <p className="course-desc muted small">{track.description}</p>

      {track.isPublished ? (
        <>
          <div className="course-meta">
            <span className="pill">
              📦 {track.modules.length} module{track.modules.length === 1 ? '' : 's'}
            </span>
            <span className="pill">
              📚 {lessonCount} lesson{lessonCount === 1 ? '' : 's'}
            </span>
            <span className="pill">🎂 Ages {track.ageRange}</span>
          </div>

          <div className="course-progress">
            <ProgressBar
              value={done}
              max={lessonCount || 1}
              tone="mint"
              label={`${done}/${lessonCount}`}
            />
          </div>

          <span className="course-cta">
            {track.requiresPayment
              ? `Unlock for ₹${((track.pricePaise ?? 0) / 100).toFixed(0)}/month →`
              : finished
                ? 'Revisit the course →'
                : done > 0
                  ? 'Continue learning →'
                  : 'Start the course →'}
          </span>
        </>
      ) : (
        <>
          <div className="course-meta">
            <span className="pill">🎂 Ages {track.ageRange}</span>
          </div>
          <div className="course-soon">
            <span aria-hidden="true">🚧</span>
            <div>
              <strong>Coming soon!</strong>
              <p className="small muted">
                Finish Cyber Smart Kids while we build it.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (!track.isPublished) {
    return (
      <div className={`course-card is-locked t-${track.color}`} aria-disabled="true">
        {body}
      </div>
    );
  }

  // An unpaid course sends them to checkout rather than into the lessons.
  return (
    <Link
      to={track.requiresPayment ? `/checkout?course=${track.slug}` : `/learn/course/${track.slug}`}
      className={`course-card t-${track.color}`}
    >
      {body}
    </Link>
  );
}

export default function CourseSelect() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dash, setDash] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.tracks(), api.studentDashboard()])
      .then(([t, d]) => {
        setData(t);
        setDash(d);
      })
      .catch((err) => setError(err.message));
  }, []);

  if (error) return <Alert kind="bad">{error}</Alert>;
  if (!data || !dash) return <Loader label="Getting your courses ready…" />;

  const stats = dash.stats ?? {
    xp: 0, lessonsCompleted: 0, totalLessons: 0, badgesEarned: 0, badgesTotal: 0, minutesSpent: 0,
  };

  return (
    <>
      {/* ---------- hero ---------- */}
      <section className="hero">
        <div className="hero-text">
          <p className="hero-hi">{greeting()},</p>
          <h1>{user.fullName.split(' ')[0]}! 👋</h1>
          <p className="muted">
            {stats.lessonsCompleted === 0
              ? 'Pick a course below and start your first cyber adventure.'
              : stats.lessonsCompleted >= stats.totalLessons
                ? 'You finished every lesson! You are officially a Cyber Hero. 🏆'
                : `You have finished ${stats.lessonsCompleted} of ${stats.totalLessons} lessons. Keep going!`}
          </p>
        </div>

        <div className="hero-cub">
          <CubMascot avatar={user.avatar} mood="cheer" size={172} className="anim-float" />
          <div className="hero-xp">
            <span aria-hidden="true">⭐</span> {stats.xp} XP
          </div>
        </div>
      </section>

      {/* ---------- stats ---------- */}
      <div className="stat-grid mt-lg">
        <Stat icon="⭐" tone="sun"   value={stats.xp} label="Total XP earned" />
        <Stat icon="📚" tone="grape" value={`${stats.lessonsCompleted}/${stats.totalLessons}`} label="Lessons completed" />
        <Stat icon="🏅" tone="mint"  value={`${stats.badgesEarned}/${stats.badgesTotal}`} label="Badges collected" />
        <Stat icon="⏱" tone="sky"   value={`${stats.minutesSpent} min`} label="Time learning" />
      </div>

      {/* ---------- courses ---------- */}
      <section className="mt-lg">
        <div className="page-head" style={{ marginBottom: '1.2rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>📖 My courses</h2>
            <p style={{ margin: '0.3rem 0 0' }}>Choose a course to see its modules and lessons.</p>
          </div>
          <Link to="/learn/badges" className="btn btn-ghost">🏅 My badges</Link>
        </div>

        <div className="course-grid">
          {data.tracks.map((track) => (
            <CourseCard key={track.slug} track={track} />
          ))}
        </div>
      </section>
    </>
  );
}
